/**
 * Enhanced Subscription Service
 * Handles creator subscriptions with time tracking, games, and locked posts
 */

import api from './api';
import { sortSubscriptionPlansByTier } from '../utils/subscriptionTiers';
import { getCheckoutReturnPath } from '../utils/checkoutReturn';

// Types
export interface GameAccess {
  game_type: string;
  play_count: number | null;
  is_unlimited: boolean;
}

export interface SubscriptionPlan {
  id: number;
  creator_id: number;
  name: string;
  description: string | null;
  price: number;
  currency?: 'USD';
  duration: 'daily' | 'weekly' | 'monthly' | 'yearly';
  duration_days: number;
  stream_time_minutes: number | null;
  is_unlimited_stream_time: boolean;
  locked_posts_count: number;
  is_unlimited_locked_posts: boolean;
  game_access: GameAccess[];
  can_chat: boolean;
  can_watch_live: boolean;
  can_watch_prelive: boolean;
  can_play_games: boolean;
  is_active: boolean;
  created_at: string;
}

export interface GameUsage {
  game_type: string;
  plays_remaining: number;
  plays_used: number;
  is_unlimited: boolean;
  can_play: boolean;
}

export interface Subscription {
  id: number;
  subscriber_id: number;
  creator_id: number;
  plan_id: number | null;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  price_paid: number;
  currency?: 'USD';
  started_at: string;
  expires_at: string;
  cancelled_at?: string | null;
  auto_renew: boolean;
  stream_time_remaining_seconds: number | null;
  is_unlimited_stream_time: boolean;
  locked_posts_remaining: number | null;
  is_unlimited_locked_posts: boolean;
  game_usage: GameUsage[] | null;
  creator_name: string | null;
  plan_name: string | null;
}

export interface SubscriptionCheck {
  is_subscribed: boolean;
  subscription: {
    id: number;
    plan_id: number;
    plan_name: string | null;
    expires_at: string;
    auto_renew?: boolean;
    active_plans?: Array<{
      id: number;
      plan_id: number;
      plan_name: string | null;
      started_at: string;
      expires_at: string;
      auto_renew?: boolean;
    }>;
    stream_subscription_id?: number | null;
    stream_time_remaining_seconds: number | null;
    is_unlimited_stream_time: boolean;
    locked_posts_subscription_id?: number | null;
    locked_posts_remaining: number | null;
    is_unlimited_locked_posts: boolean;
    game_usage: Record<string, {
      subscription_id?: number;
      plays_remaining: number;
      plays_used: number;
      is_unlimited: boolean;
      has_plays: boolean;
    }>;
    can_chat: boolean;
    can_watch_live: boolean;
    can_watch_prelive: boolean;
    can_play_games: boolean;
  } | null;
}

export interface SubscriptionStats {
  active_subscribers: number;
  active_subscribers_by_plan?: Record<string, number>;
  currency?: 'USD';
  total_earnings: number;
  total_gross: number;
  total_commission: number;
  month_earnings: number;
  month_gross: number;
  month_commission: number;
  commission_rate: number;
  earnings_by_tier: Array<{
    tier: string;
    earnings: number;
    count: number;
  }>;
}

export interface StreamTimeResult {
  remaining_seconds: number;
  is_unlimited: boolean;
  warning: boolean;
  expired: boolean;
  subscription_id?: number | null;
  switched_subscription?: boolean;
}

export interface GamePlayResult {
  plays_remaining: number;
  is_unlimited: boolean;
  can_play: boolean;
  message?: string;
  subscription_id?: number | null;
}

export interface UnlockPostResult {
  success: boolean;
  message: string;
  remaining: number | null;
  already_unlocked: boolean;
}

export interface SubscriptionCheckoutResponse {
  action: 'checkout' | 'reactivated';
  subscription_id: number;
  checkout_session_id?: string | null;
  checkout_url?: string | null;
  message: string;
}

export interface CreatePlanData {
  name: string;
  description?: string;
  price: number;
  duration: 'daily' | 'weekly' | 'monthly' | 'yearly';
  is_active?: boolean;
  stream_time_minutes?: number;
  is_unlimited_stream_time?: boolean;
  locked_posts_count?: number;
  is_unlimited_locked_posts?: boolean;
  game_access?: GameAccess[];
  can_chat?: boolean;
  can_watch_live?: boolean;
  can_watch_prelive?: boolean;
  can_play_games?: boolean;
}

export interface SubscriptionSettings {
  platform_commission_percent: number;
  default_stream_time_minutes: number;
  default_locked_posts_count: number;
  default_game_plays_per_game: number;
  time_warning_minutes: number;
  min_subscription_price: number;
  max_subscription_price: number;
  allow_free_tier: boolean;
  allow_unlimited_options: boolean;
}

// API Functions

// Plan Management
export const createPlan = async (data: CreatePlanData): Promise<SubscriptionPlan> => {
  const response = await api.post('/subscriptions/plans', data);
  return response.data;
};

export const getMyPlans = async (): Promise<SubscriptionPlan[]> => {
  const response = await api.get('/subscriptions/plans/my');
  return sortSubscriptionPlansByTier(response.data || []);
};

export const getCreatorPlans = async (creatorId: number): Promise<SubscriptionPlan[]> => {
  const response = await api.get(`/subscriptions/plans/creator/${creatorId}`);
  return sortSubscriptionPlansByTier(response.data || []);
};

export const updatePlan = async (planId: number, data: Partial<CreatePlanData>): Promise<SubscriptionPlan> => {
  const response = await api.put(`/subscriptions/plans/${planId}`, data);
  return response.data;
};

export const deactivatePlan = async (planId: number): Promise<void> => {
  await api.delete(`/subscriptions/plans/${planId}`);
};

export const reactivatePlan = async (planId: number): Promise<SubscriptionPlan> => {
  const response = await api.put(`/subscriptions/plans/${planId}`, { is_active: true });
  return response.data;
};

// Subscription Management
export const startSubscriptionCheckout = async (
  creatorId: number,
  planId: number,
  autoRenew: boolean = true,
  returnPath: string = getCheckoutReturnPath()
): Promise<SubscriptionCheckoutResponse> => {
  const response = await api.post('/subscriptions/checkout-session', {
    creator_id: creatorId,
    plan_id: planId,
    auto_renew: autoRenew,
    return_path: returnPath
  });
  return response.data;
};

export const redirectToSubscriptionCheckout = (checkout: SubscriptionCheckoutResponse): boolean => {
  if (checkout.checkout_url) {
    window.location.href = checkout.checkout_url;
    return true;
  }
  return false;
};

export const subscribe = async (
  creatorId: number,
  planId: number,
  autoRenew: boolean = true,
  returnPath?: string
): Promise<SubscriptionCheckoutResponse> => {
  return startSubscriptionCheckout(creatorId, planId, autoRenew, returnPath);
};

export const syncSubscriptionCheckoutSession = async (checkoutSessionId: string): Promise<Subscription> => {
  const response = await api.post(`/subscriptions/checkout-session/${encodeURIComponent(checkoutSessionId)}/sync`);
  return response.data;
};

export const renewSubscription = async (subscriptionId: number): Promise<Subscription> => {
  const response = await api.post(`/subscriptions/${subscriptionId}/renew`);
  return response.data;
};

export const checkSubscription = async (creatorId: number): Promise<SubscriptionCheck> => {
  const response = await api.get(`/subscriptions/check/${creatorId}`);
  return response.data;
};

export const getMySubscriptions = async (activeOnly: boolean = true): Promise<Subscription[]> => {
  const response = await api.get('/subscriptions/my', {
    params: { active_only: activeOnly }
  });
  return response.data;
};

export const getMySubscribers = async (activeOnly: boolean = true): Promise<Subscription[]> => {
  const response = await api.get('/subscriptions/subscribers', {
    params: { active_only: activeOnly }
  });
  return response.data;
};

export const cancelSubscription = async (subscriptionId: number): Promise<void> => {
  await api.post(`/subscriptions/cancel/${subscriptionId}`);
};

export const updateAutoRenew = async (subscriptionId: number, autoRenew: boolean): Promise<Subscription> => {
  const response = await api.post(`/subscriptions/${subscriptionId}/auto-renew`, {
    auto_renew: autoRenew
  });
  return response.data;
};

export const getSubscriptionStats = async (): Promise<SubscriptionStats> => {
  const response = await api.get('/subscriptions/stats');
  return response.data;
};

export const getSubscriptionUsage = async (subscriptionId: number): Promise<any> => {
  const response = await api.get(`/subscriptions/${subscriptionId}/usage`);
  return response.data;
};

// Stream Time Tracking
export const trackStreamTime = async (
  subscriptionId: number,
  secondsWatched: number,
  streamSessionId?: number
): Promise<StreamTimeResult> => {
  const response = await api.post(`/subscriptions/${subscriptionId}/track-time`, {
    seconds_watched: secondsWatched,
    stream_session_id: streamSessionId
  });
  return response.data;
};

export const getRemainingStreamTime = async (creatorId: number): Promise<{
  has_subscription: boolean;
  remaining_seconds: number;
  is_unlimited: boolean;
  subscription_id?: number | null;
}> => {
  const response = await api.get(`/subscriptions/stream-time/${creatorId}`);
  return response.data;
};

// Game Access
export const useGamePlay = async (subscriptionId: number, gameType: string): Promise<GamePlayResult> => {
  const response = await api.post(`/subscriptions/${subscriptionId}/use-game`, {
    game_type: gameType
  });
  return response.data;
};

export const getGamePlaysRemaining = async (creatorId: number, gameType?: string): Promise<{
  has_subscription: boolean;
  games: Record<string, {
    plays_remaining: number;
    plays_used: number;
    is_unlimited: boolean;
    can_play: boolean;
  }>;
}> => {
  const response = await api.get(`/subscriptions/game-plays/${creatorId}`, {
    params: gameType ? { game_type: gameType } : undefined
  });
  return response.data;
};

export const checkGameAccess = async (creatorId: number, gameType: string): Promise<{
  can_play: boolean;
  reason?: string;
  plays_remaining?: number;
  is_unlimited?: boolean;
}> => {
  const response = await api.get(`/subscriptions/check-game/${creatorId}/${gameType}`);
  return response.data;
};

// Locked Posts
export const lockPost = async (postId: number, previewUrl?: string): Promise<void> => {
  await api.post(`/subscriptions/posts/${postId}/lock`, {
    preview_url: previewUrl
  });
};

export const unlockPostCreator = async (postId: number): Promise<void> => {
  await api.post(`/subscriptions/posts/${postId}/unlock-creator`);
};

export const unlockPostSubscriber = async (subscriptionId: number, postId: number): Promise<UnlockPostResult> => {
  const response = await api.post('/subscriptions/unlock-post', {
    subscription_id: subscriptionId,
    post_id: postId
  });
  return response.data;
};

export const getMyLockedPosts = async (): Promise<Array<{
  id: number;
  content: string | null;
  post_type: string | null;
  is_locked: boolean;
  lock_preview_url: string | null;
  created_at: string | null;
}>> => {
  const response = await api.get('/subscriptions/locked-posts');
  return response.data;
};

export const checkPostUnlocked = async (creatorId: number, postId: number): Promise<{
  is_unlocked: boolean;
}> => {
  const response = await api.get(`/subscriptions/check-post-unlocked/${creatorId}/${postId}`);
  return response.data;
};

// Admin Settings
export const getSubscriptionSettings = async (): Promise<SubscriptionSettings> => {
  const response = await api.get('/subscriptions/admin/settings');
  return response.data;
};

export const updateSubscriptionSettings = async (settings: Partial<SubscriptionSettings>): Promise<void> => {
  await api.put('/subscriptions/admin/settings', settings);
};

// Go Live Request
export const requestGoLive = async (creatorId: number): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await api.post(`/subscriptions/request-go-live/${creatorId}`);
  return response.data;
};

// Helper functions
export const formatStreamTime = (seconds: number): string => {
  if (seconds < 0) return 'Unlimited';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

export const getGameTypeName = (gameType: string): string => {
  const names: Record<string, string> = {
    'tic_tac_toe': 'Tic Tac Toe',
    'spin_bottle': 'Spin the Bottle',
    'strip_poker': 'Strip Poker',
    'spin_wheel': 'Spin the Wheel',
    'prediction': 'Prediction'
  };
  return names[gameType] || gameType;
};

// Service object with all functions
const subscriptionService = {
  createPlan,
  getMyPlans,
  getCreatorPlans,
  updatePlan,
  deactivatePlan,
  reactivatePlan,
  startSubscriptionCheckout,
  redirectToSubscriptionCheckout,
  subscribe,
  syncSubscriptionCheckoutSession,
  renewSubscription,
  checkSubscription,
  getMySubscriptions,
  getMySubscribers,
  cancelSubscription,
  updateAutoRenew,
  getSubscriptionStats,
  getSubscriptionUsage,
  trackStreamTime,
  getRemainingStreamTime,
  useGamePlay,
  getGamePlaysRemaining,
  checkGameAccess,
  lockPost,
  unlockPostCreator,
  unlockPostSubscriber,
  getMyLockedPosts,
  checkPostUnlocked,
  getSubscriptionSettings,
  updateSubscriptionSettings,
  requestGoLive,
  formatStreamTime,
  getGameTypeName
};

export default subscriptionService;
